import type { ApiRequestConfig, ApiError } from "../clients/awsApiClient.ts";
import { useApiQuery, useApiPostMutation } from "./useApiQuery.ts";
import type { PreferredLanguage } from "./useUserApi.ts";

export type Reasoning = {
    PK: string;
    SK: string;
    anomalyCount: number;
    confidenceLevel: string;
    crossFactorSignals: string;
    dataQualityAlerts: string;
    factorConfidence: string;
    factorDrivers: string;
    generatedAt: string;
    marketContext: string;
    modelId: string;
    nearBoundary: string;
    notableShifts: string;
    portfolioChanges: string;
    rebalanceDate: string;
    rebalanceRecommendation: string;
    rebalanceSignal: string;
    strategyId: string;
    version: string;
}

export type ChatMessage = {
    role: "user" | "assistant";
    content: string;
}

export type ChatRequest = {
    strategyId?: string; // Omitted for user portfolios - the backend takes the owner from the token
    modelId: string;
    rebalanceDate: string;
    question: string;
    conversationHistory?: ChatMessage[];
    language?: PreferredLanguage; // Language the LLM answers in; the backend defaults to "en"
}

export type ChatResponse = {
    answer: string;
    strategyId: string;
    modelId: string;
    rebalanceDate: string;
}

export type GenerateReasoningRequest = {
    modelId: string; // Composite key: {userId}_{universe}_{period}_{modelId}
    rebalanceDate: string;
    force?: boolean; // Optional: force regeneration even if reasoning exists
    language?: PreferredLanguage; // Language the report is written in; the backend defaults to "en"
}

export type GenerateReasoningResponse = {
    message: string;
    strategyId?: string; // Not returned for user portfolios
    modelId: string;
    rebalanceDate: string;
    status: 'processing'; // Always 'processing' since Lambda runs asynchronously
}

/**
 * Get portfolio reasoning by strategy ID, model ID, period, and date
 * Constructs the full composite PK from strategyId (without period suffix) + period + modelId
 */
export function usePortfolioReasoning(strategyId: string, modelId: string, period: string, date: string, config?: ApiRequestConfig) {
    // Build composite key: remove period suffix from strategyId and insert actual period, then append modelId
    // Example: admin_sp500_M + 5Y + ar1logprice22 → admin_sp500_5Y_ar1logprice22
    let compositePK = strategyId;

    // Remove trailing period pattern (_M, _1Y, _5Y, _10Y, etc.)
    compositePK = compositePK.replace(/_[0-9]*[MYWQD]$/, '');

    // Append period and modelId to create full composite key
    compositePK = `${compositePK}_${period}_${modelId}`;

    return useApiQuery<{ item: Reasoning }>({
        path: `/reasoning/${compositePK}/${date}`,
        config,
        queryKey: ["reasoning", compositePK, date],
        enabled: !!strategyId && !!modelId && !!period && !!date,
        retry: (failureCount, error: ApiError) => {
            // Don't retry on 404 - data simply doesn't exist
            if (error?.status === 404) return false;
            // Default: retry up to 3 times for other errors
            return failureCount < 3;
        },
    });
}

/**
 * Get reasoning for a user portfolio by its model UUID and rebalance date.
 * The backend resolves the owner from the auth token (stored PK: `{userId}#{modelId}`).
 */
export function useUserPortfolioReasoning(modelId: string, date: string, config?: ApiRequestConfig) {
    return useApiQuery<{ item: Reasoning }>({
        path: `/reasoning/${modelId}/${date}`,
        config,
        queryKey: ["reasoning", modelId, date],
        enabled: !!modelId && !!date,
        retry: (failureCount, error: ApiError) => {
            if (error?.status === 404) return false;
            return failureCount < 3;
        },
    });
}

/**
 * Chat with reasoning LLM
 */
export function useChatWithReasoning() {
    return useApiPostMutation<ChatResponse, ChatRequest>({
        getPath: () => "/reasoning/chat",
        getConfig: (variables) => ({
            body: {
                strategyId: variables.strategyId,
                modelId: variables.modelId,
                rebalanceDate: variables.rebalanceDate,
                question: variables.question,
                conversationHistory: variables.conversationHistory || [],
                language: variables.language,
            },
        }),
    });
}

/**
 * Trigger reasoning generation for a specific model and rebalance date
 * Note: modelId should be the composite key: {userId}_{universe}_{period}_{modelId}
 * Example: admin_sp500_5Y_marketcap
 */
export function useGenerateReasoning() {
    return useApiPostMutation<GenerateReasoningResponse, GenerateReasoningRequest>({
        getPath: (variables) => `/reasoning/${variables.modelId}/${variables.rebalanceDate}`,
        getConfig: (variables) => ({
            body: {
                force: variables.force ?? false,
                language: variables.language,
            },
        }),
    });
}

/**
 * Trigger reasoning generation for a user portfolio (model UUID + rebalance date).
 * Regeneration is not offered for user portfolios, so the backend always uses force=false.
 */
export function useGenerateUserPortfolioReasoning() {
    return useApiPostMutation<GenerateReasoningResponse, Omit<GenerateReasoningRequest, "force">>({
        getPath: (variables) => `/reasoning/${variables.modelId}/${variables.rebalanceDate}`,
        getConfig: (variables) => ({ body: { language: variables.language } }),
    });
}
